import React from 'react';

// ============================================================
// components/form/FormFields.tsx – Campos reutilizables
// ============================================================

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

export const FormInput: React.FC<InputProps> = ({ label, error, hint, required, className, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      {...props}
      className={`
        w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50
        placeholder-slate-300 outline-none transition-all duration-150
        focus:ring-2 focus:ring-blue-500 focus:border-transparent
        disabled:opacity-50
        ${error ? 'border-red-400 bg-red-50' : 'border-slate-200'}
        ${className ?? ''}
      `}
    />
    {error && <span className="text-red-500 text-xs">{error}</span>}
    {!error && hint && <span className="text-slate-400 text-xs">{hint}</span>}
  </div>
);

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
  required?: boolean;
  options: { value: string | number; label: string }[];
  placeholder?: string;
}

export const FormSelect: React.FC<SelectProps> = ({
  label, error, required, options, placeholder, className, ...props
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <select
      {...props}
      className={`
        w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50
        outline-none transition-all duration-150
        focus:ring-2 focus:ring-blue-500 focus:border-transparent
        disabled:opacity-50
        ${error ? 'border-red-400 bg-red-50' : 'border-slate-200'}
        ${className ?? ''}
      `}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    {error && <span className="text-red-500 text-xs">{error}</span>}
  </div>
);

interface EmailInputProps {
  label:     string;
  name:      string;
  value:     string;      // email completo almacenado en el form
  domain:    string;      // sufijo p. ej. "@adamsmith.edu.pe"
  onChange:  (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?:    string;
  required?: boolean;
}

/**
 * Campo de correo dividido: el usuario escribe la parte local y el
 * dominio se muestra fijo como sufijo.
 * Excepción: si domain === '@gmail.com' se renderiza como input libre
 * (permite cualquier dominio).
 */
export const FormEmailInput: React.FC<EmailInputProps> = ({
  label, name, value, domain, onChange, error, required,
}) => {
  const isLibre = domain === '@gmail.com';

  // ── Modo libre: dominio @gmail.com → acepta cualquier email ──
  if (isLibre) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <input
          type="email"
          name={name}
          value={value}
          onChange={onChange}
          placeholder="correo@ejemplo.com"
          autoComplete="off"
          className={`
            w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50
            placeholder-slate-300 outline-none transition-all duration-150
            focus:ring-2 focus:ring-blue-500 focus:border-transparent
            ${error ? 'border-red-400 bg-red-50' : 'border-slate-200'}
          `}
        />
        {error && <span className="text-red-500 text-xs">{error}</span>}
      </div>
    );
  }

  // ── Modo institucional: dominio fijo como sufijo ──────────────
  const localPart = value.includes('@') ? value.split('@')[0] : value;

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const local = e.target.value.replace(/@.*/g, ''); // evitar que peguen un @ adicional
    const synthetic = {
      ...e,
      target: { ...e.target, name, value: local ? `${local}${domain}` : '' },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(synthetic);
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className={`flex items-center rounded-lg border overflow-hidden bg-slate-50
        focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent
        ${error ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}>
        <input
          type="text"
          name={name}
          value={localPart}
          onChange={handleLocalChange}
          placeholder="usuario"
          autoComplete="off"
          className="flex-1 min-w-0 px-3 py-2.5 text-sm text-slate-800 bg-transparent
                     placeholder-slate-300 outline-none"
        />
        <span className="px-2.5 py-2.5 text-sm font-medium text-slate-500 bg-slate-100
                         border-l border-slate-200 whitespace-nowrap select-none">
          {domain}
        </span>
      </div>
      {error && <span className="text-red-500 text-xs">{error}</span>}
    </div>
  );
};

interface MultiSelectCardProps {
  label: string;
  error?: string;
  required?: boolean;
  items: { id: number; label: string; sublabel?: string }[];
  selected: number[];
  onToggle: (id: number) => void;
}

export const MultiSelectCards: React.FC<MultiSelectCardProps> = ({
  label, error, required, items, selected, onToggle,
}) => (
  <div className="flex flex-col gap-2">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      <span className="ml-2 normal-case font-normal text-slate-400">
        ({selected.length} seleccionado{selected.length !== 1 ? 's' : ''})
      </span>
    </label>
    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
      {items.map(item => {
        const active = selected.includes(item.id);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onToggle(item.id)}
            className={`
              text-left px-3 py-2.5 rounded-lg border text-sm transition-all duration-150
              ${active
                ? 'border-blue-500 bg-blue-50 text-blue-800'
                : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors
                ${active ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                {active && (
                  <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                    <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div>
                <p className="font-semibold leading-tight">{item.label}</p>
                {item.sublabel && <p className="text-xs text-slate-400">{item.sublabel}</p>}
              </div>
            </div>
          </button>
        );
      })}
      {items.length === 0 && (
        <p className="col-span-2 text-slate-400 text-sm text-center py-4">
          Seleccione un grado primero
        </p>
      )}
    </div>
    {error && <span className="text-red-500 text-xs">{error}</span>}
  </div>
);
